import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Routes, Route } from 'react-router-dom';
import './App.css';
import { LanguageProvider, useTranslation, formatPrice } from './i18n';

// Get API URL from environment variable or use default
const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

// Poll a Celery task every 2s until it reaches a terminal state.
// One interval per task id; the effect cleans it up on unmount or when the
// task leaves the 'processing' state (progress updates don't recreate it).
function useTaskPolling(task, setTask, onCompleted, onProgress) {
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;

  const taskId = task ? task.id : null;
  const isActive = !!task && task.status === 'processing' && !String(taskId).startsWith('temp-');

  useEffect(() => {
    if (!isActive) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/status`);
        if (!response.ok || cancelled) return;
        const statusData = await response.json();
        if (cancelled) return;

        if (statusData.status === 'completed') {
          console.log(`✅ Task ${taskId} completed`);
          setTask(prev => (prev && prev.id === taskId) ? { ...prev, status: 'completed', result: statusData.result } : prev);
          if (onCompletedRef.current) onCompletedRef.current(statusData);
          setTimeout(() => setTask(prev => (prev && prev.id === taskId) ? null : prev), 15000);
        } else if (statusData.status === 'failed') {
          console.error(`❌ Task ${taskId} failed:`, statusData.error);
          setTask(prev => (prev && prev.id === taskId) ? { ...prev, status: 'failed', error: statusData.error } : prev);
          setTimeout(() => setTask(prev => (prev && prev.id === taskId) ? null : prev), 5000);
        } else if (statusData.status === 'processing') {
          setTask(prev => (prev && prev.id === taskId) ? { ...prev, progress: statusData.progress } : prev);
          if (onProgressRef.current) onProgressRef.current(statusData.progress);
        }
      } catch (error) {
        console.error(`Error polling task ${taskId}:`, error);
      }
    };

    poll(); // immediate check, also covers tasks that finished before mount
    const interval = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [taskId, isActive, setTask]);
}



function ComparisonSlider({ beforeVideo, afterVideo }) {
  const [dividerPosition, setDividerPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const beforeVideoRef = useRef(null);
  const afterVideoRef = useRef(null);
  const containerRef = useRef(null);

  // Autoplay videos on mount
  useEffect(() => {
    const beforeVideo = beforeVideoRef.current;
    const afterVideo = afterVideoRef.current;
    
    if (beforeVideo && afterVideo) {
      beforeVideo.play().catch(console.error);
      afterVideo.play().catch(console.error);
    }
  }, []);

  const handleTimeUpdate = () => {
    if (beforeVideoRef.current && afterVideoRef.current) {
      const time = beforeVideoRef.current.currentTime;
      if (Math.abs(afterVideoRef.current.currentTime - time) > 0.1) {
        afterVideoRef.current.currentTime = time;
      }
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      setDividerPosition(Math.max(0, Math.min(100, percentage)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div className="comparison-container" ref={containerRef}>
      <video
        ref={beforeVideoRef}
        className="comparison-video video-before"
        src={beforeVideo}
        autoPlay
        muted
        loop
        playsInline
        onTimeUpdate={handleTimeUpdate}
      />
      <video
        ref={afterVideoRef}
        className="comparison-video video-after"
        src={afterVideo}
        autoPlay
        muted
        loop
        playsInline
        onTimeUpdate={handleTimeUpdate}
        style={{ clipPath: `inset(0 0 0 ${dividerPosition}%)` }}
      />
      <div 
        className="comparison-slider"
        style={{ left: `${dividerPosition}%` }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
        <div className="slider-handle">⇄</div>
      </div>
      <div className="comparison-labels">
        <span className="comparison-label label-before">⚠️ Before</span>
        <span className="comparison-label label-after">✓ After</span>
      </div>
    </div>
  );
}

function UploadInterface({ onUploadComplete }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [repoUuid, setRepoUuid] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [totalFrames, setTotalFrames] = useState(0);
  const [authUser, setAuthUser] = useState(undefined); // undefined = unknown, null = signed out, {email} = signed in
  const [showLoginModal, setShowLoginModal] = useState(false);
  const navigate = useNavigate();
  const { t, lang, pricing } = useTranslation();

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/me`, { credentials: 'include' });
        setAuthUser(response.ok ? await response.json() : null);
      } catch {
        setAuthUser(null);
      }
    })();
  }, []);

  const signOut = async () => {
    if (!window.confirm(t('login.confirmSignOut', authUser?.email))) return;
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (error) {
      console.error('❌ Error signing out:', error);
    }
    setAuthUser(null);
  };

  const handleFileSelect = (file) => {
    console.log('📂 File selected:', {
      name: file?.name,
      type: file?.type,
      size: file?.size
    });
    
    if (file && file.type.startsWith('video/')) {
      console.log('✅ Valid video file selected');
      setSelectedFile(file);
      setUploadStatus('');
      // Automatically start upload when file is selected
      handleUpload(file);
    } else {
      console.warn('❌ Invalid file type selected:', file?.type);
      setUploadStatus(t('upload.invalidFile'));
      setSelectedFile(null);
    }
  };

  const handleFileInputChange = (e) => {
    console.log('🖱️ File input change event');
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  // PUT one part straight to S3. fetch() gives no upload progress, so this stays XHR.
  // The ETag identifies the part when completing the upload; S3 only exposes it to
  // JS if the bucket's CORS rule lists ETag in ExposeHeaders.
  const uploadPart = (url, blob, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(e.loaded);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag');
        if (etag) resolve(etag);
        else reject(new Error('S3 did not expose ETag — check the bucket CORS ExposeHeaders'));
      } else {
        reject(new Error(`Part upload failed with status ${xhr.status}`));
      }
    });
    xhr.addEventListener('error', () => reject(new Error('Network error')));
    xhr.addEventListener('abort', () => reject(new Error('Aborted')));
    xhr.send(blob);
  });

  const handleUpload = useCallback(async (file) => {
    if (!file) return;

    console.log(`🚀 Starting upload for file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
    const startTime = Date.now();

    setUploadStatus(t('upload.statusUploading'));
    setUploadProgress(0);
    setUploadSpeed('');
    setIsUploading(true);

    let session = null;
    try {
      // 1. Ask the API to open a multipart upload and hand back presigned part URLs.
      const createRes = await fetch(`${API_BASE_URL}/api/uploads/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, size: file.size }),
      });
      if (!createRes.ok) {
        const detail = await createRes.json().catch(() => ({}));
        throw new Error(detail.detail || `Could not start upload (${createRes.status})`);
      }
      session = await createRes.json();
      console.log(`🎯 Upload ${session.uuid}: ${session.urls.length} parts of ${(session.part_size / 1024 / 1024).toFixed(0)} MB`);

      // 2. PUT the parts directly to S3, a few at a time. This is why multi-GB
      //    videos work at all: nothing goes through Cloudflare, which rejects
      //    request bodies over 100 MB.
      const loaded = new Array(session.urls.length).fill(0);
      const reportProgress = () => {
        const sent = loaded.reduce((a, b) => a + b, 0);
        const elapsed = (Date.now() - startTime) / 1000;
        const speed = (sent / 1024 / 1024 / elapsed).toFixed(2);
        setUploadProgress((sent / file.size) * 100);
        setUploadSpeed(`${speed} MB/s`);
      };

      const parts = new Array(session.urls.length);
      let next = 0;
      const worker = async () => {
        while (next < session.urls.length) {
          const i = next++;
          const start = i * session.part_size;
          const blob = file.slice(start, Math.min(start + session.part_size, file.size));
          const etag = await uploadPart(session.urls[i], blob, (bytes) => {
            loaded[i] = bytes;
            reportProgress();
          });
          loaded[i] = blob.size;
          reportProgress();
          parts[i] = { PartNumber: i + 1, ETag: etag };
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, session.urls.length) }, worker));

      console.log(`🚀 All parts uploaded — finalizing...`);
      setUploadStatus(t('upload.statusCompleteProcessing'));

      // 3. Completing the upload assembles the object and queues decomposition.
      const completeRes = await fetch(`${API_BASE_URL}/api/uploads/${session.uuid}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: session.filename, upload_id: session.upload_id, parts }),
      });
      if (!completeRes.ok) throw new Error(`Could not finalize upload (${completeRes.status})`);
      const response = await completeRes.json();

      const uploadTime = ((Date.now() - startTime) / 1000).toFixed(2);
      console.log(`✅ Upload completed in ${uploadTime}s — uuid ${response.uuid}`);

      setRepoUuid(response.uuid);
      navigate(`/${response.uuid}`);
      if (onUploadComplete) onUploadComplete(response);

      setUploadStatus(t('upload.statusSuccess'));
      setIsUploading(false);
      setUploadProgress(100);
      setUploadSpeed('');
    } catch (err) {
      const uploadTime = ((Date.now() - startTime) / 1000).toFixed(2);
      console.error(`💥 Upload failed after ${uploadTime}s:`, err);
      // Release the parts already stored, so a cancelled upload isn't billed
      // until the bucket's lifecycle rule sweeps it.
      if (session) {
        fetch(`${API_BASE_URL}/api/uploads/${session.uuid}/abort`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: session.filename, upload_id: session.upload_id }),
        }).catch(() => {});
      }
      setUploadStatus(t('upload.statusFailed', err.message));
      setIsUploading(false);
      setUploadSpeed('');
    }
  }, [t]);

  const handleDrop = (e) => {
    e.preventDefault();
    console.log('📥 File dropped');
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    console.log('📤 File drag over');
  };

  const freePrice = formatPrice(0, pricing.currency, lang);
  const fullPrice = formatPrice(pricing.priceCents, pricing.currency, lang);

  return (
    <div className="app">
      {/* Navigation Bar */}
      <nav className="main-nav">
        <div className="nav-container">
          <a href="#" className="nav-logo">
            <div className="logo-icon">⚡</div>
            No flash
          </a>
          <ul className="nav-links">
            <li><a href="#features" onClick={(e) => { e.preventDefault(); document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); }}>{t('nav.features')}</a></li>
            <li><a href="#how-it-works" onClick={(e) => { e.preventDefault(); document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }); }}>{t('nav.howItWorks')}</a></li>
            <li><a href="#pricing" onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}>{t('nav.pricing')}</a></li>
            <li><a href="#faq" onClick={(e) => { e.preventDefault(); document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }); }}>{t('nav.faq')}</a></li>
            <li><a href="#contact" onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}>{t('nav.contact')}</a></li>
          </ul>
          <div className="nav-actions">
            <LanguageToggle />
            <a href="#upload" className="nav-cta" onClick={(e) => { e.preventDefault(); document.getElementById('upload')?.scrollIntoView({ behavior: 'smooth' }); }}>
              <span>📤</span> {t('nav.uploadVideo')}
            </a>
            {authUser !== undefined && (
              authUser ? (
                <button
                  className="nav-signin logout"
                  onClick={signOut}
                  title={t('nav.signOut', authUser.email)}
                >
                  <LogoutIcon />
                </button>
              ) : (
                <button
                  className="nav-signin"
                  onClick={() => setShowLoginModal(true)}
                  title={t('nav.signIn')}
                >
                  <UserIcon />
                </button>
              )
            )}
          </div>
        </div>
      </nav>

      {showLoginModal && (
        <div className="modal-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="download-modal login-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowLoginModal(false)} aria-label="Close">×</button>
            <h2>{t('login.title')}</h2>
            <p>{t('login.descHome')}</p>
            <LoginForm redirect="/" />
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1>{t('hero.title')}</h1>
            <p>{t('hero.desc')}</p>
            <div className="hero-buttons">
              <a href="#upload" className="btn-primary" onClick={(e) => { e.preventDefault(); document.getElementById('upload')?.scrollIntoView({ behavior: 'smooth' }); }}>
                <span>🚀</span> {t('hero.tryFree')}
              </a>
              <a href="#how-it-works" className="btn-secondary" onClick={(e) => { e.preventDefault(); document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }); }}>
                <span>▶</span> {t('hero.seeHow')}
              </a>
            </div>
          </div>
          <div className="comparison-wrapper">
            <ComparisonSlider
              beforeVideo="/videos/before.mp4"
              afterVideo="/videos/after.mp4"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="section-header">
          <h2>{t('features.title')}</h2>
          <p>{t('features.subtitle')}</p>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon glitch">🔄</div>
            <h3>{t('features.smartTitle')}</h3>
            <p>{t('features.smartDesc')}</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon interpolation">🎬</div>
            <h3>{t('features.accuracyTitle')}</h3>
            <p>{t('features.accuracyDesc')}</p>
          </div>
          <div className="feature-card coming-soon">
            <div className="feature-icon strobe">⚡</div>
            <h3>{t('features.strobeTitle')} <span className="coming-soon-badge">{t('features.strobeBadge')}</span></h3>
            <p>{t('features.strobeDesc')}</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works">
        <div className="section-header">
          <h2>{t('howItWorks.title')}</h2>
          <p>{t('howItWorks.subtitle')}</p>
        </div>
        <div className="steps-container">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>{t('howItWorks.step1Title')}</h3>
              <p>{t('howItWorks.step1Desc')}</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>{t('howItWorks.step2Title')}</h3>
              <p>{t('howItWorks.step2Desc')}</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>{t('howItWorks.step3Title')}</h3>
              <p>{t('howItWorks.step3Desc')}</p>
              <div className="step-visual selection-examples">
                <div className="selection-example">
                  <span className="example-label">{t('howItWorks.doLabel')}</span>
                  <div className="example-frames">
                    <div className="frame frame-good">{t('howItWorks.good')}</div>
                    <div className="frame frame-bad selected">{t('howItWorks.bad')}</div>
                    <div className="frame frame-bad selected">{t('howItWorks.bad')}</div>
                    <div className="frame frame-good">{t('howItWorks.good')}</div>
                  </div>
                </div>
                <div className="selection-example">
                  <span className="example-label">{t('howItWorks.dontLabel')}</span>
                  <div className="example-frames">
                    <div className="frame frame-good">{t('howItWorks.good')}</div>
                    <div className="frame frame-bad selected">{t('howItWorks.bad')}</div>
                    <div className="frame frame-bad">{t('howItWorks.bad')}</div>
                    <div className="frame frame-good">{t('howItWorks.good')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="step">
            <div className="step-number">4</div>
            <div className="step-content">
              <h3>{t('howItWorks.step4Title')}</h3>
              <p>{t('howItWorks.step4Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases" id="use-cases">
        <div className="section-header">
          <h2>{t('useCases.title')}</h2>
          <p>{t('useCases.subtitle')}</p>
        </div>
        <div className="use-cases-grid">
          <div className="use-case-card">
            <img src="/images/wedding-photo.jpg" alt="Wedding" />
            <div className="use-case-content">
              <h3>{t('useCases.weddingsTitle')}</h3>
              <p>{t('useCases.weddingsDesc')}</p>
            </div>
          </div>
          <div className="use-case-card">
            <img src="/images/party-photo.jpg" alt="Party celebration" />
            <div className="use-case-content">
              <h3>{t('useCases.partiesTitle')}</h3>
              <p>{t('useCases.partiesDesc')}</p>
            </div>
          </div>
          <div className="use-case-card">
            <img src="/images/concert-photo.jpg" alt="Concert" />
            <div className="use-case-content">
              <h3>{t('useCases.concertsTitle')}</h3>
              <p>{t('useCases.concertsDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing" id="pricing">
        <div className="section-header">
          <h2>{t('pricing.title')}</h2>
          <p>{t('pricing.subtitle')}</p>
        </div>
        <div className="pricing-grid">
          <div className="pricing-card">
            <h3>{t('pricing.freeTitle')}</h3>
            <div className="price">{freePrice} <span>{t('pricing.perVideo')}</span></div>
            <ul>
              <li><span className="check-icon">✓</span> {t('pricing.freeItem1')}</li>
              <li><span className="check-icon">✓</span> {t('pricing.freeItem2')}</li>
            </ul>
            <a href="#upload" className="btn btn-secondary" style={{width: '100%', textAlign: 'center', justifyContent: 'center'}} onClick={(e) => { e.preventDefault(); document.getElementById('upload')?.scrollIntoView({ behavior: 'smooth' }); }}>{t('pricing.getStarted')}</a>
          </div>
          <div className="pricing-card featured">
            <h3>{t('pricing.fullTitle')}</h3>
            <div className="price">{fullPrice} <span>{t('pricing.perVideo')}</span></div>
            <ul>
              <li><span className="check-icon">✓</span> {t('pricing.fullItem1')}</li>
              <li><span className="check-icon">✓</span> {t('pricing.fullItem2')}</li>
            </ul>
            <a href="#upload" className="btn btn-primary" style={{width: '100%', textAlign: 'center', justifyContent: 'center'}} onClick={(e) => { e.preventDefault(); document.getElementById('upload')?.scrollIntoView({ behavior: 'smooth' }); }}>{t('pricing.getStarted')}</a>
          </div>
          <div className="pricing-card">
            <h3>{t('pricing.enterpriseTitle')}</h3>
            <div className="price">{t('pricing.enterpriseCustom')}</div>
            <ul>
              <li><span className="check-icon">✓</span> {t('pricing.enterpriseItem1')}</li>
              <li><span className="check-icon">✓</span> {t('pricing.enterpriseItem2')}</li>
            </ul>
            <a href="#contact" className="btn btn-secondary" style={{width: '100%', textAlign: 'center', justifyContent: 'center'}} onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}>{t('pricing.contactUs')}</a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq" id="faq">
        <div className="section-header">
          <h2>{t('faq.title')}</h2>
          <p>{t('faq.subtitle')}</p>
        </div>
        <div className="faq-container">
          <FAQItem question={t('faq.q1')} answer={t('faq.a1')} />
          <FAQItem question={t('faq.q2')} answer={t('faq.a2')} />
          <FAQItem question={t('faq.q3')} answer={t('faq.a3')} />
          <FAQItem question={t('faq.q4')} answer={t('faq.a4')} />
          <FAQItem question={t('faq.q5')} answer={t('faq.a5')} />
          <FAQItem question={t('faq.q6')} answer={t('faq.a6')} />
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact" id="contact">
        <div className="section-header">
          <h2>{t('contact.title')}</h2>
          <p>{t('contact.subtitle')}</p>
        </div>
        <ContactForm />
      </section>

      {/* Upload Section */}
      <section className="upload-section" id="upload">
        <div className="upload-container">
          <div className="section-header">
            <h2>{t('upload.title')}</h2>
            <p>{t('upload.subtitle')}</p>
          </div>
          <div
            className={`upload-zone ${selectedFile ? 'has-file' : ''} ${isUploading ? 'uploading' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            id="uploadZone"
          >
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              onChange={handleFileInputChange}
              id="file-input"
              style={{ display: 'none' }}
            />
            {!selectedFile ? (
              <>
                <div className="upload-icon">📤</div>
                <h3>{t('upload.dropHere')}</h3>
                <p>{t('upload.orBrowse')}</p>
                <label htmlFor="file-input" className="btn-primary" style={{ display: 'inline-flex', marginTop: '1rem' }}>
                  <span>📁</span> {t('upload.selectFile')}
                </label>
                <div className="file-types">
                  <span className="file-type">MP4</span>
                  <span className="file-type">MOV</span>
                  <span className="file-type">WEBM</span>
                  <span className="file-type">Max 2GB</span>
                </div>
              </>
            ) : (
              <div>
                <h3>{t('upload.selected', selectedFile.name)}</h3>
                <p>{t('upload.size', formatFileSize(selectedFile.size))}</p>
                {isUploading && <p>{t('upload.uploadingAuto')}</p>}
                {isProcessing && <p>{t('upload.processingVideo')}</p>}
              </div>
            )}
          </div>
          {isUploading && (
            <div className="processing-demo visible">
              <div className="processing-status">
                <div className="status-header">
                  <h4>{t('upload.uploadingHeader')}</h4>
                  <span className="status-badge">{t('upload.inProgress')}</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                </div>
                {uploadSpeed && <p>{t('upload.speed', uploadSpeed)}</p>}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="footer-bottom">
          <p>{t('footer.copyright')}</p>
        </div>
      </footer>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <div className="faq-question" onClick={() => setIsOpen(!isOpen)}>
        <span>{question}</span>
        <span className="faq-toggle">▼</span>
      </div>
      <div className="faq-answer">
        <div className="faq-answer-content">
          {answer}
        </div>
      </div>
    </div>
  );
}

function RestoreIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
}

function ZoomIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ZoomOutIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  );
}

function CompareIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="2.4" />
      <path d="M4 4 H12 V20 H4 Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

function formatFrameTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s.toFixed(3).padStart(6, '0')}`;
}

function formatDate(epochSeconds) {
  return new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric'
  });
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Landing page for magic links: exchanges the emailed token for a session
// cookie, then sends the user back to where they were (e.g. their project
// with the download modal open)
function AuthVerify() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        if (response.ok) {
          const data = await response.json();
          navigate(data.redirect || '/', { replace: true });
        } else {
          setError(t('authVerify.invalidLink'));
        }
      } catch {
        setError(t('authVerify.networkError'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="auth-verify">
      <p>{error || t('authVerify.signingIn')}</p>
    </div>
  );
}

function UserIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function LanguageToggle() {
  const { lang, setLang } = useTranslation();
  const nextLang = lang === 'en' ? 'fr' : 'en';
  return (
    <button
      className="lang-toggle"
      onClick={() => setLang(nextLang)}
      title={nextLang === 'fr' ? 'Passer en français' : 'Switch to English'}
      aria-label={nextLang === 'fr' ? 'Passer en français' : 'Switch to English'}
    >
      {lang === 'en' ? '🇺🇸' : '🇫🇷'}
    </button>
  );
}

// Email form requesting a magic sign-in link; the link lands on /auth/verify
// which forwards to `redirect`
function LoginForm({ redirect }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { t } = useTranslation();

  const submit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/request_link`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, redirect })
      });
      if (response.ok) {
        setSent(true);
      } else {
        console.error('❌ Failed to request sign-in link:', await response.text());
        alert(t('login.errorSend'));
      }
    } catch (error) {
      console.error('❌ Error requesting sign-in link:', error);
      alert(t('login.errorSendDetail', error.message));
    }
  };

  if (sent) {
    return (
      <p className="login-sent">
        {t('login.sentPrefix')} <strong>{email}</strong>{t('login.sentSuffix')}
      </p>
    );
  }
  return (
    <form className="modal-login-form" onSubmit={submit}>
      <input
        type="email"
        required
        placeholder={t('login.emailPlaceholder')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <button type="submit" className="btn btn-primary">{t('login.submit')}</button>
    </form>
  );
}

function ContactForm() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const { t } = useTranslation();

  const submit = async (e) => {
    e.preventDefault();
    const data = new FormData(e.target);
    const payload = {
      name: data.get('name'),
      email: data.get('email'),
      subject: data.get('subject'),
      message: data.get('message'),
    };
    setSending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setSent(true);
      } else {
        console.error('❌ Failed to send message:', await response.text());
        alert(t('contact.errorSend'));
      }
    } catch (error) {
      console.error('❌ Error sending message:', error);
      alert(t('contact.errorSendDetail', error.message));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <p className="login-sent" style={{ textAlign: 'center' }}>
        {t('contact.sent')}
      </p>
    );
  }
  return (
    <form className="contact-form" onSubmit={submit}>
      <div className="contact-row">
        <input type="text" name="name" placeholder={t('contact.namePlaceholder')} required />
        <input type="email" name="email" placeholder={t('contact.emailPlaceholder')} required />
      </div>
      <input type="text" name="subject" placeholder={t('contact.subjectPlaceholder')} required />
      <textarea name="message" rows="5" placeholder={t('contact.messagePlaceholder')} required />
      <button type="submit" className="btn-primary" disabled={sending}>
        {sending ? t('contact.sending') : t('contact.send')}
      </button>
    </form>
  );
}

function FrameDisplay({ decompositionTask: parentDecompositionTask, setDecompositionTask, recompositionTask: parentRecompositionTask, setRecompositionTask }) {
  const { repoUuid } = useParams();
  const navigate = useNavigate();
  const { t, lang, pricing } = useTranslation();

  console.log(`🎬 FrameDisplay component initialized`);
  console.log(`🎬 repoUuid from params:`, repoUuid);
  
  const [frames, setFrames] = useState([]);
  const [frameNumbers, setFrameNumbers] = useState([]);
  const [selectedFrames, setSelectedFrames] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalFrames, setTotalFrames] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [frameCache, setFrameCache] = useState(new Map()); // Cache frames by page number
  const [loadingPages, setLoadingPages] = useState(new Set()); // Track which pages are loading
  const [processingStatus, setProcessingStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [lastKnownFrameCount, setLastKnownFrameCount] = useState(0);
  const [lastKnownFrameNames, setLastKnownFrameNames] = useState('');
  const [runningTasks, setRunningTasks] = useState([]); // Changed to array for multiple tasks
  const [forceRefreshCounter, setForceRefreshCounter] = useState(0); // Force refresh trigger
  const [gpuStatus, setGpuStatus] = useState(null); // {status: 'hot'|'warming'|'cold'|'unknown', started_at}
  const [now, setNow] = useState(Date.now()); // ticks every 1s only while warming, for the elapsed timer
  const [cacheBuster, setCacheBuster] = useState(Date.now()); // Cache buster for images
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [outputs, setOutputs] = useState(null); // {full, hd, fresh} from /api/{uuid}/outputs
  const [authUser, setAuthUser] = useState(undefined); // undefined = unknown, null = signed out, {email} = signed in
  const [showProjectsMenu, setShowProjectsMenu] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [projects, setProjects] = useState(null); // [{uuid, name, created_at, modified_at}] from /api/auth/projects
  const [fps, setFps] = useState(null); // from video_info.json, for frame timestamps
  const [compareFrame, setCompareFrame] = useState(null); // frame number shown in the compare modal
  const [restoring, setRestoring] = useState(false);
  const [correctedFrames, setCorrectedFrames] = useState(new Set()); // frame numbers overwritten by interpolation
  const [peekFrame, setPeekFrame] = useState(null); // frame temporarily showing its original (hold-to-peek)
  const [confirmDeleteUuid, setConfirmDeleteUuid] = useState(null); // project pending delete confirmation
  
  const framesPerPage = 20;

  // Handle frame selection
  const toggleFrameSelection = useCallback((frameNumber) => {
    setSelectedFrames(prev => {
      const newSet = new Set(prev);
      if (newSet.has(frameNumber)) {
        newSet.delete(frameNumber);
      } else {
        newSet.add(frameNumber);
      }
      return newSet;
    });
  }, []);

  // Get selected frame numbers as sorted array
  const selectedFrameNumbers = useMemo(() => {
    return Array.from(selectedFrames).sort((a, b) => a - b);
  }, [selectedFrames]);



  // Handle 'pending' case - wait for actual UUID
  useEffect(() => {
    if (repoUuid === 'pending') {
      console.log(`⏳ Waiting for actual UUID to be assigned...`);
      setLoading(true);
    }
  }, [repoUuid]);

  const fetchTotalFrames = useCallback(async (uuid) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/${uuid}/ls/?start=0&end=0`);
      if (response.ok) {
        const data = await response.json();
        setTotalFrames(data.frames.total);
      }
    } catch (err) {
      console.error('Failed to get total frames count:', err);
    }
  }, []);

  const checkProcessingStatus = useCallback(async (uuid) => {
    if (!uuid) return;
    
    try {
      console.log(`🔍 Checking processing status for ${uuid}...`);
      const response = await fetch(`${API_BASE_URL}/api/${uuid}/status`);
      const data = await response.json();
      
      console.log(`📊 Processing status: ${JSON.stringify(data)}`);
      
      if (data.processing_complete) {
        console.log(`🎉 Video processing completed! Found ${data.frame_count} frames`);
        setIsProcessing(false);
        setProcessingStatus('Processing complete!');
      } else {
        // Check if we have a decomposition task running
        if (parentDecompositionTask && parentDecompositionTask.status === 'processing') {
          console.log(`⏳ Decomposition in progress... (${data.frame_count} frames, metadata: ${data.has_metadata})`);
          setProcessingStatus(`Decomposing video... (${data.frame_count} frames available)`);
          setIsProcessing(true);
        } else {
          console.log(`⏳ Still processing... (${data.frame_count} frames, metadata: ${data.has_metadata})`);
          setProcessingStatus(`Processing video... (${data.frame_count} frames available)`);
          setIsProcessing(true);
        }
      }
    } catch (error) {
      console.error(`❌ Error checking processing status: ${error}`);
      setProcessingStatus('Error checking status');
    }
  }, [parentDecompositionTask]);

  const loadFrames = useCallback(async (uuid, page = null, skipLoadingState = false) => {
    if (!uuid) {
      console.log(`❌ loadFrames called with no UUID`);
      return;
    }
    
    const targetPage = page !== null ? page : currentPage;
    
    // Check cache first
    if (frameCache.has(targetPage)) {
      console.log(`📦 Using cached frames for page ${targetPage}`);
      const cached = frameCache.get(targetPage);
      setFrames(cached.frames);
      setFrameNumbers(cached.frameNumbers);
      if (!skipLoadingState) {
        setLoading(false);
      }
      return;
    }
    
    // Check if already loading this page
    if (loadingPages.has(targetPage)) {
      console.log(`⏳ Page ${targetPage} already loading, skipping...`);
      return;
    }
    
    try {
      console.log(`🖼️ Loading frames for ${uuid}, page ${targetPage}...`);
      if (!skipLoadingState && targetPage === currentPage) {
        setLoading(true);
      }
      setLoadingPages(prev => new Set(prev).add(targetPage));
      const startTime = Date.now();
      
      // Calculate pagination
      const start = (targetPage - 1) * framesPerPage;
      const end = start + framesPerPage - 1;
      
      console.log(`📄 Fetching frames from ${start} to ${end}...`);
      const response = await fetch(`${API_BASE_URL}/api/${uuid}/ls/?start=${start}&end=${end}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log(`📊 Response data:`, data);
      
      // Use the raw frame paths directly (skip video_info.json and audio.wav)
      const framePaths = data.filenames
        .filter(filename => filename.includes('frame_') && filename.endsWith('.jpg'));
      
      const loadTime = ((Date.now() - startTime) / 1000).toFixed(2);
      console.log(`✅ Loaded ${framePaths.length} frames in ${loadTime}s`);
      
      // Cache the frames
      const cacheEntry = {
        frames: framePaths,
        frameNumbers: data.frames.numbers
      };
      setFrameCache(prev => {
        const newCache = new Map(prev);
        newCache.set(targetPage, cacheEntry);
        // Limit cache size to 5 pages
        if (newCache.size > 5) {
          const firstKey = newCache.keys().next().value;
          newCache.delete(firstKey);
        }
        return newCache;
      });
      
      // Only update current frames if this is the current page
      if (targetPage === currentPage) {
        console.log(`📊 Setting frames state: ${framePaths.length} frames`);
        setFrames(framePaths);
        setFrameNumbers(data.frames.numbers);
        setLoading(false);
      }
      
      setLoadingPages(prev => {
        const newSet = new Set(prev);
        newSet.delete(targetPage);
        return newSet;
      });
      
      console.log(`✅ Frame state updated successfully`);
      
      // Set baseline frame count and names (only for current page)
      if (targetPage === currentPage) {
        setLastKnownFrameCount(data.frames.total);
        setLastKnownFrameNames(JSON.stringify(data));
      }
      
      // Update total frame count from the response
      console.log(`📊 Total frames from backend: ${data.frames.total}`);
      setTotalFrames(data.frames.total);
      setCorrectedFrames(new Set(data.frames.corrected || []));
    } catch (error) {
      console.error(`❌ Error loading frames: ${error}`);
      setError(t('frameDisplay.errorLoadFrames', error.message));
      setLoading(false);
      setLoadingPages(prev => {
        const newSet = new Set(prev);
        newSet.delete(targetPage);
        return newSet;
      });
    }
  }, [currentPage, framesPerPage, frameCache, loadingPages, t]);

  const checkForChanges = useCallback(async (uuid) => {
    if (!uuid) return false;
    
    try {
      console.log(`🔍 Checking for changes by comparing API response...`);
      
      // Calculate current page frame range
      const start = (currentPage - 1) * framesPerPage;
      const end = start + framesPerPage - 1;
      
      const response = await fetch(`${API_BASE_URL}/api/${uuid}/ls/?start=${start}&end=${end}`);
      if (response.ok) {
        const data = await response.json();
        
        // Simple comparison: stringify the entire response
        const currentResponse = JSON.stringify(data);
        
        console.log(`📊 Response size: ${currentResponse.length} chars`);
        
        if (lastKnownFrameNames && lastKnownFrameNames !== currentResponse) {
          console.log(`🔄 API response changed!`);
          setLastKnownFrameNames(currentResponse);
          return true;
        } else if (!lastKnownFrameNames) {
          console.log(`📅 First load - setting baseline response`);
          setLastKnownFrameNames(currentResponse);
        } else {
          console.log(`✅ No changes detected - response identical`);
        }
      }

    } catch (error) {
      console.error(`❌ Error checking for changes: ${error}`);
    }
    return false;
  }, [lastKnownFrameNames, currentPage, framesPerPage]);

  // Add a global function to reset baseline (for debugging)
  useEffect(() => {
    window.resetBaseline = () => {
      setLastKnownFrameNames(null);
      console.log('🔄 Baseline reset - next check will set new baseline');
    };
  }, []);

  // Pagination functions
  const goToPage = useCallback((page) => {
    // Check if page is cached, if so update immediately without loading state
    if (frameCache.has(page)) {
      const cached = frameCache.get(page);
      setFrames(cached.frames);
      setFrameNumbers(cached.frameNumbers);
    }
    setCurrentPage(page);
  }, [frameCache]);

  const goToNextPage = useCallback(() => {
    if (currentPage < Math.ceil(totalFrames / framesPerPage)) {
      goToPage(currentPage + 1);
    }
  }, [currentPage, totalFrames, framesPerPage, goToPage]);

  const goToPrevPage = useCallback(() => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, goToPage]);

  // Refresh page data after a task completes. Kept in a "latest ref" so the
  // polling effects don't depend on these often-recreated callbacks.
  const refreshAfterTaskRef = useRef(() => {});
  const lastRefreshedMilestoneRef = useRef(-1); // floor(log2(frameCount)) last refreshed at, for decompose
  refreshAfterTaskRef.current = () => {
    setForceRefreshCounter(prev => prev + 1);
    setCacheBuster(Date.now());
    loadFrames(repoUuid);
    fetchTotalFrames(repoUuid);
    checkProcessingStatus(repoUuid);
    // Resync checkForChanges' baseline to what we just fetched, so the
    // periodic auto-refresh poller doesn't see its own stale baseline a
    // moment later and fire a second, redundant refresh right behind this one.
    checkForChanges(repoUuid);
  };

  // Poll all active interpolation tasks with a single shared interval
  const activeInterpolationIds = runningTasks
    .filter(t => t.status === 'running' || t.status === 'processing')
    .map(t => t.id)
    .join(',');

  useEffect(() => {
    if (!activeInterpolationIds) return;
    const ids = activeInterpolationIds.split(',');

    let cancelled = false;
    const poll = async () => {
      for (const taskId of ids) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/status`);
          if (!response.ok || cancelled) continue;
          const statusData = await response.json();
          if (cancelled) return;

          if (statusData.status === 'completed') {
            console.log(`✅ Interpolation task ${taskId} completed`);
            setRunningTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'completed', result: statusData.result } : t));
            refreshAfterTaskRef.current();
            setTimeout(() => setRunningTasks(prev => prev.filter(t => t.id !== taskId)), 15000);
          } else if (statusData.status === 'failed') {
            console.error(`❌ Interpolation task ${taskId} failed:`, statusData.error);
            setRunningTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'failed', error: statusData.error } : t));
            setTimeout(() => setRunningTasks(prev => prev.filter(t => t.id !== taskId)), 5000);
          } else if (statusData.status === 'processing') {
            setRunningTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'processing', progress: statusData.progress } : t));
          }
        } catch (error) {
          console.error(`Error polling task ${taskId}:`, error);
        }
      }
    };

    poll();
    const interval = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [activeInterpolationIds]);

  // Ask the server which recomposed files exist and whether they are up to date
  const fetchOutputs = useCallback(async (uuid) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/${uuid}/outputs`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setOutputs(data);
        return data;
      }
    } catch (error) {
      console.error('❌ Error fetching outputs:', error);
    }
    return null;
  }, []);

  const signOut = useCallback(async () => {
    if (!window.confirm(t('login.confirmSignOut', authUser?.email))) return;
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (error) {
      console.error('❌ Error signing out:', error);
    }
    setAuthUser(null);
    setProjects(null);
    navigate('/'); // signing out always returns to the homepage
  }, [navigate, authUser, t]);

  // Start recomposition (full + HD) so the files are ready before the user downloads
  const prepareVideo = useCallback(async (uuid) => {
    try {
      console.log(`🎬 Preparing download files for ${uuid}`);

      // Set recomposition task state
      setRecompositionTask({
        id: `temp-${Date.now()}`,
        status: 'submitting',
        startTime: Date.now()
      });

      // Start video recomposition
      const response = await fetch(`${API_BASE_URL}/api/${uuid}/recompose/`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Video recomposition started:', result);

        // Update task with real ID; the polling effect picks it up
        setRecompositionTask(prev => ({
          ...prev,
          id: result.task_id,
          status: 'processing'
        }));

      } else {
        const error = await response.text();
        console.error('❌ Failed to start video recomposition:', error);
        setRecompositionTask(null);
      }
    } catch (error) {
      console.error('❌ Error preparing video:', error);
      setRecompositionTask(null);
    }
  }, [setRecompositionTask]);

    const downloadCompletedVideo = useCallback(async (uuid, videoPath) => {
    console.log(`📥 Downloading video: ${videoPath}`);

    // The server sends Content-Disposition: attachment, so the browser saves
    // the file directly without opening a tab
    const a = document.createElement('a');
    a.href = `${API_BASE_URL}/${videoPath}?download=1`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    console.log('✅ Video download started');
  }, []);

  // Send the user to Stripe Checkout to unlock the full-resolution download
  const startCheckout = useCallback(async (uuid) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/${uuid}/checkout`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;   // redirect to Stripe's hosted page
      } else if (data.paid) {
        downloadCompletedVideo(uuid, outputs.full);   // already paid
      } else {
        console.error('❌ Checkout failed:', data);
        alert(t('downloadModal.errorCheckout'));
      }
    } catch (error) {
      console.error('❌ Error starting checkout:', error);
      alert(t('downloadModal.errorCheckoutDetail', error.message));
    }
  }, [downloadCompletedVideo, outputs, t]);

  // Poll the decomposition / recomposition tasks (single interval each, with
  // cleanup; the immediate first poll also covers already-completed tasks)
  useTaskPolling(parentDecompositionTask, setDecompositionTask, () => refreshAfterTaskRef.current(),
    (progress) => {
      // Refresh at frame-count milestones (8, 16, 32, 64, ...) instead of on
      // every new frame: frequent early on, when a handful more frames is a
      // big relative jump worth showing right away, backing off on its own
      // later as each doubling represents proportionally less new content.
      const current = progress?.current || 0;
      if (current < 8) return;
      const milestone = Math.floor(Math.log2(current));
      if (milestone > lastRefreshedMilestoneRef.current) {
        lastRefreshedMilestoneRef.current = milestone;
        refreshAfterTaskRef.current();
      }
    });
  useTaskPolling(parentRecompositionTask, setRecompositionTask, () => {
    fetchOutputs(repoUuid);
  });

  const recomposing = !!parentRecompositionTask &&
    (parentRecompositionTask.status === 'submitting' || parentRecompositionTask.status === 'processing');

  // Arriving via a magic link (or any /uuid?download=1 URL) reopens the download modal.
  // Returning from Stripe adds ?session_id=… — confirm the payment, then download.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('download') === '1') {
      setShowDownloadModal(true);
    }
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/${repoUuid}/confirm`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
        });
        const data = await response.json();
        if (data.paid) {
          const out = await fetchOutputs(repoUuid);
          if (out?.full) downloadCompletedVideo(repoUuid, out.full);
        }
      } catch (error) {
        console.error('❌ Payment confirmation failed:', error);
      }
      // Drop session_id from the URL so a refresh doesn't re-confirm
      window.history.replaceState({}, '', `/${repoUuid}?download=1`);
    })();
  }, [repoUuid, fetchOutputs, downloadCompletedVideo]);

  // Check the session on mount; a signed-in visit claims the project for the
  // user so it shows up in their project list
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/me`, { credentials: 'include' });
        if (cancelled) return;
        if (response.ok) {
          const user = await response.json();
          // claim before exposing the session so the project list (fetched on
          // authUser change) already includes the current project
          await fetch(`${API_BASE_URL}/api/${repoUuid}/claim`, { method: 'POST', credentials: 'include' })
            .catch(() => {});
          if (!cancelled) setAuthUser(user);
        } else {
          setAuthUser(null);
        }
      } catch (error) {
        console.error('❌ Error checking session:', error);
        if (!cancelled) setAuthUser(null);
      }
    })();
    return () => { cancelled = true; };
  }, [repoUuid]);

  // Read the fps from the repo's metadata for frame timestamps (retries while
  // processing in case the metadata isn't extracted yet)
  useEffect(() => {
    if (fps !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/static/${repoUuid}/video_info.json`);
        if (!response.ok || cancelled) return;
        const info = await response.json();
        const [num, den] = (info.video?.r_frame_rate || '').split('/');
        const value = parseFloat(num) / parseFloat(den || '1');
        if (!cancelled && isFinite(value) && value > 0) setFps(value);
      } catch (error) {
        console.error('❌ Error reading video metadata:', error);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoUuid, isProcessing]);

  // When the download modal opens, reuse existing up-to-date files or start preparing them
  useEffect(() => {
    if (!showDownloadModal) return;
    let cancelled = false;
    (async () => {
      const data = await fetchOutputs(repoUuid);
      if (!cancelled && data && !data.fresh && !recomposing) {
        prepareVideo(repoUuid);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDownloadModal, repoUuid]);

  // Load the user's project list once signed in (the selector shows the current
  // project's name), and refresh it whenever the dropdown opens
  useEffect(() => {
    if (!authUser || (!showProjectsMenu && projects !== null)) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/projects`, { credentials: 'include' });
        if (!cancelled && response.ok) {
          setProjects((await response.json()).projects);
        }
      } catch (error) {
        console.error('❌ Error loading projects:', error);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showProjectsMenu, authUser]);

  const restoreFrame = useCallback(async (frameNumber) => {
    setRestoring(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/${repoUuid}/frames/${frameNumber}/restore`, { method: 'POST' });
      if (response.ok) {
        refreshAfterTaskRef.current(); // reload images and the corrected-frames set
        setCompareFrame(null);
      } else {
        alert(t('compareModal.errorRestore', await response.text()));
      }
    } catch (error) {
      alert(t('compareModal.errorRestoreNetwork', error.message));
    } finally {
      setRestoring(false);
    }
  }, [repoUuid, t]);

  const deleteProject = useCallback(async (uuid) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/${uuid}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) {
        const remaining = projects ? projects.filter(p => p.uuid !== uuid) : [];
        setProjects(remaining);
        setConfirmDeleteUuid(null);
        if (uuid === repoUuid) {
          navigate(remaining.length > 0 ? `/${remaining[0].uuid}` : '/');
        }
      } else {
        alert(t('frameDisplay.errorDelete', await response.text()));
      }
    } catch (error) {
      alert(t('frameDisplay.errorDeleteNetwork', error.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoUuid, navigate, projects]);

  // Load frames when page changes or force refresh triggered
  // Clear cache on force refresh
  useEffect(() => {
    if (forceRefreshCounter > 0) {
      console.log(`🔄 Clearing frame cache due to force refresh`);
      setFrameCache(new Map());
    }
  }, [forceRefreshCounter]);

  useEffect(() => {
    console.log(`🎬 useEffect triggered with repoUuid: ${repoUuid}`);
    console.log(`🎬 Current page: ${currentPage}`);
    console.log(`🎬 Force refresh counter: ${forceRefreshCounter}`);
    console.log(`🎬 Is processing: ${isProcessing}`);
    
    if (repoUuid && repoUuid !== 'pending') {
      console.log(`🎬 Loading frames for repo: ${repoUuid}`);
      // Load current page (skip loading state if cached and not force refresh)
      const isCached = frameCache.has(currentPage) && forceRefreshCounter === 0;
      loadFrames(repoUuid, currentPage, isCached);
      
      // Pre-load adjacent pages in background
      const totalPages = Math.ceil(totalFrames / framesPerPage);
      if (currentPage > 1 && !frameCache.has(currentPage - 1)) {
        loadFrames(repoUuid, currentPage - 1, true);
      }
      if (currentPage < totalPages && !frameCache.has(currentPage + 1)) {
        loadFrames(repoUuid, currentPage + 1, true);
      }
    } else if (repoUuid === 'pending') {
      console.log(`⏳ Skipping frame load - waiting for actual UUID`);
    } else {
      console.log(`❌ No repoUuid available for frame loading`);
    }
  }, [repoUuid, currentPage, forceRefreshCounter, loadFrames, frameCache, totalFrames, framesPerPage]);

  // Check processing status periodically
  useEffect(() => {
    if (repoUuid && repoUuid !== 'pending') {
      console.log(`🔍 useEffect triggered - checking processing status for repo: ${repoUuid}`);
      console.log(`🔍 Is processing: ${isProcessing}`);
      
      // Check processing status once when component loads
      checkProcessingStatus(repoUuid);
    } else if (repoUuid === 'pending') {
      console.log(`⏳ Skipping processing status check - waiting for actual UUID`);
    } else {
      console.log(`❌ No repoUuid available for processing status check`);
    }
  }, [repoUuid, checkProcessingStatus]);

  // Auto-refresh while something is in flight; stops once processing is
  // complete and no tasks are running (the manual Refresh button remains)
  const hasActiveTasks =
    runningTasks.some(t => t.status !== 'completed' && t.status !== 'failed') ||
    !!(parentDecompositionTask && parentDecompositionTask.status === 'processing') ||
    !!(parentRecompositionTask && parentRecompositionTask.status === 'processing');

  useEffect(() => {
    if (!repoUuid || repoUuid === 'pending') return;
    if (!isProcessing && !hasActiveTasks) return;

    // Adaptive interval instead of a fixed one: starts fast (1s) since new
    // frames land steadily while ffmpeg is decomposing, doubles up to 8s
    // during quiet stretches to cut needless polling, and resets to 1s the
    // moment new content actually appears (more usually follows right behind).
    console.log(`🔄 Auto-refresh active (adaptive 1s-8s) - processing or tasks in flight`);
    let cancelled = false;
    let delay = 1000;
    const MAX_DELAY_MS = 8000;
    let timer;

    const tick = async () => {
      if (cancelled) return;
      const hasChanges = await checkForChanges(repoUuid);
      if (cancelled) return;
      if (hasChanges) {
        console.log(`🔄 Changes detected! Triggering refresh...`);
        setForceRefreshCounter(prev => prev + 1);
        setCacheBuster(Date.now());
        checkProcessingStatus(repoUuid);
        fetchTotalFrames(repoUuid);
        loadFrames(repoUuid);
        delay = 1000;
      } else {
        delay = Math.min(delay * 2, MAX_DELAY_MS);
      }
      if (!cancelled) timer = setTimeout(tick, delay);
    };

    timer = setTimeout(tick, delay);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [repoUuid, isProcessing, hasActiveTasks, checkProcessingStatus, loadFrames, fetchTotalFrames, checkForChanges]);

  // GPU status indicator: polled independently of task activity (it's useful
  // ambient info even when nothing is processing, e.g. "will my next click
  // be instant or trigger a cold start?").
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/gpu/status`);
        if (response.ok && !cancelled) setGpuStatus(await response.json());
      } catch (error) {
        console.error('❌ Error fetching GPU status:', error);
      }
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Separate 1s clock for the warming-up timer display, decoupled from the
  // 5s status poll so the displayed elapsed time ticks smoothly.
  useEffect(() => {
    if (gpuStatus?.status !== 'warming') return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [gpuStatus?.status]);

  // ~2-3 min observed cold boot; the countdown is an estimate, not a promise,
  // so it's clamped at 0 rather than going negative if a boot runs long.
  const GPU_WARMUP_ESTIMATE_SECONDS = 150;
  const gpuElapsedSeconds = gpuStatus?.status === 'warming' && gpuStatus.started_at
    ? Math.max(0, Math.floor(now / 1000 - gpuStatus.started_at))
    : null;
  const gpuRemainingSeconds = gpuElapsedSeconds !== null
    ? Math.max(0, GPU_WARMUP_ESTIMATE_SECONDS - gpuElapsedSeconds)
    : null;
  const gpuProgress = gpuElapsedSeconds !== null
    ? Math.min(1, gpuElapsedSeconds / GPU_WARMUP_ESTIMATE_SECONDS)
    : 0;
  const gpuIcon = { hot: '🔥', warming: '⏳', cold: '❄️' }[gpuStatus?.status] || '🔄';
  const gpuTitle = {
    hot: t('gpu.hot'),
    warming: t('gpu.warming'),
    cold: t('gpu.cold'),
  }[gpuStatus?.status] || t('gpu.default');

  return (
    <div className="app frame-display-page">
      {/* Navigation Bar */}
      <nav className="main-nav">
        <div className="nav-container">
          <a href="/" className="nav-logo" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
            <div className="logo-icon">⚡</div>
            No flash
          </a>
          <div className="projects-menu">
              <button
                onClick={() => { setShowProjectsMenu(v => !v); setConfirmDeleteUuid(null); }}
                className="project-trigger"
                title={t('nav.changeProject')}
              >
                {(() => {
                  const current = projects?.find(p => p.uuid === repoUuid);
                  return current ? (
                    <>
                      <span className="project-name">{current.name}</span>
                      <span className="project-uuid">{current.uuid}</span>
                    </>
                  ) : (
                    <span className="project-uuid">{repoUuid}</span>
                  );
                })()}
                <span className="trigger-caret">▾</span>
              </button>
              {showProjectsMenu && (
                <>
                  <div
                    className="dropdown-backdrop"
                    onClick={() => { setShowProjectsMenu(false); setConfirmDeleteUuid(null); }}
                  />
                  <div className="projects-dropdown">
                    {authUser === undefined ? (
                      <p className="dropdown-note">{t('frameDisplay.checkingSession')}</p>
                    ) : authUser === null ? (
                      <div className="dropdown-login">
                        <p>{t('frameDisplay.signInToKeep')}</p>
                        <LoginForm redirect={`/${repoUuid}`} />
                      </div>
                    ) : projects === null ? (
                      <p className="dropdown-note">{t('frameDisplay.loadingProjects')}</p>
                    ) : projects.length === 0 ? (
                      <p className="dropdown-note">{t('frameDisplay.noProjects')}</p>
                    ) : (
                      <ul>
                        {projects.map((project) => (
                          <li key={project.uuid} className={project.uuid === repoUuid ? 'current' : ''}>
                            <button
                              className="project-row"
                              onClick={() => {
                                setShowProjectsMenu(false);
                                if (project.uuid !== repoUuid) navigate(`/${project.uuid}`);
                              }}
                            >
                              <span className="project-name">{project.name}</span>
                              <span className="project-uuid">{project.uuid}</span>
                              <span className="project-dates">
                                {t('frameDisplay.createdUpdated', formatDate(project.created_at), formatDate(project.modified_at))}
                              </span>
                            </button>
                            {confirmDeleteUuid === project.uuid ? (
                              <button
                                className="project-delete confirm"
                                onClick={() => deleteProject(project.uuid)}
                              >
                                {t('frameDisplay.delete')}
                              </button>
                            ) : (
                              <button
                                className="project-delete"
                                title={t('frameDisplay.deleteTitle')}
                                onClick={() => setConfirmDeleteUuid(project.uuid)}
                              >
                                ✕
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
          </div>
          <div className="frame-actions">
            <LanguageToggle />
            <button
              onClick={() => {
                setForceRefreshCounter(prev => prev + 1);
                setCacheBuster(Date.now());
                loadFrames(repoUuid);
                fetchTotalFrames(repoUuid);
              }}
              className={`gpu-status-badge gpu-${gpuStatus?.status || 'unknown'}`}
              disabled={loading}
              title={gpuTitle}
            >
              <span
                className="gpu-status-ring"
                style={gpuStatus?.status === 'warming'
                  ? { background: `conic-gradient(#f59e0b ${gpuProgress * 360}deg, rgba(245,158,11,0.18) 0deg)` }
                  : undefined}
              >
                <span className="gpu-status-icon">{gpuIcon}</span>
              </span>
              {gpuRemainingSeconds !== null && (
                <span className="gpu-status-timer">
                  {String(Math.floor(gpuRemainingSeconds / 60)).padStart(2, '0')}:{String(gpuRemainingSeconds % 60).padStart(2, '0')}
                </span>
              )}
            </button>
            <button
              onClick={() => {
                console.log('📥 Download video button clicked');
                setShowDownloadModal(true);
              }}
              className="action-button download"
              disabled={loading || isProcessing}
              title={t('frameDisplay.downloadTitle')}
            >
              {t('frameDisplay.downloadVideo')}
            </button>
            {authUser !== undefined && (
              authUser ? (
                <button
                  onClick={signOut}
                  className="action-button icon-button logout"
                  title={t('nav.signOut', authUser.email)}
                >
                  <LogoutIcon />
                </button>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="action-button icon-button"
                  title={t('nav.signIn')}
                >
                  <UserIcon />
                </button>
              )
            )}
          </div>
        </div>
      </nav>

      {showLoginModal && (
        <div className="modal-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="download-modal login-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowLoginModal(false)} aria-label="Close">×</button>
            <h2>{t('login.title')}</h2>
            <p>{t('login.descProject')}</p>
            <LoginForm redirect={`/${repoUuid}`} />
          </div>
        </div>
      )}

      {compareFrame !== null && (
        <div className="modal-overlay" onClick={() => setCompareFrame(null)}>
          <div className="frame-container zoom-card" onClick={(e) => e.stopPropagation()}>
            <img
              src={peekFrame === compareFrame
                ? `${API_BASE_URL}/api/${repoUuid}/frames/${compareFrame}/original`
                : `${API_BASE_URL}/static/${repoUuid}/frame_${String(compareFrame).padStart(6, '0')}.jpg?cb=${cacheBuster}`}
              alt={`Frame ${compareFrame}`}
              className="zoom-image"
              draggable={false}
            />
            <div className="frame-badges">
              <button
                className="frame-badge"
                title={t('compareModal.zoomOut')}
                onClick={() => setCompareFrame(null)}
              >
                <ZoomOutIcon />
              </button>
              {correctedFrames.has(compareFrame) && (
                <button
                  className="frame-badge"
                  title={t('compareModal.peekTitle')}
                  onMouseDown={() => setPeekFrame(compareFrame)}
                  onMouseUp={() => setPeekFrame(null)}
                  onMouseLeave={() => setPeekFrame(null)}
                  onTouchStart={() => setPeekFrame(compareFrame)}
                  onTouchEnd={() => setPeekFrame(null)}
                >
                  <CompareIcon />
                </button>
              )}
              {correctedFrames.has(compareFrame) && (
                <button
                  className="frame-badge"
                  title={t('compareModal.restoreTitle')}
                  disabled={restoring}
                  onClick={() => {
                    if (window.confirm(t('compareModal.restoreConfirm', compareFrame))) {
                      restoreFrame(compareFrame);
                    }
                  }}
                >
                  <RestoreIcon />
                </button>
              )}
            </div>
            {peekFrame === compareFrame && <span className="peek-tag">{t('compareModal.original')}</span>}
            <div className="frame-info">
              <span>#{compareFrame}</span>
              {correctedFrames.has(compareFrame) && <span className="corrected-dot" />}
              {fps && <span className="frame-time">{formatFrameTime((compareFrame - 1) / fps)}</span>}
            </div>
          </div>
        </div>
      )}

      {showDownloadModal && (
        <div className="modal-overlay" onClick={() => setShowDownloadModal(false)}>
          <div className="download-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowDownloadModal(false)} aria-label="Close">×</button>
            <h2>{t('downloadModal.title')}</h2>
            <p>
              {outputs?.fresh ? t('downloadModal.freshDesc') : t('downloadModal.preparingDesc')}
            </p>
            <div className="download-modal-body">
              <div className="modal-auth-panel">
                {authUser === undefined ? (
                  <p>{t('downloadModal.checkingSession')}</p>
                ) : authUser === null ? (
                  <>
                    <h3>{t('downloadModal.signInHeading')}</h3>
                    <p>{t('downloadModal.signInDesc')}</p>
                    <LoginForm redirect={`/${repoUuid}?download=1`} />
                  </>
                ) : (
                  <>
                    <h3>{t('downloadModal.signedInHeading')}</h3>
                    <p><strong>{authUser.email}</strong></p>
                    <p>{t('downloadModal.signedInDesc')}</p>
                  </>
                )}
              </div>
            <div className="download-options">
              <div className="pricing-card">
                <h3>{t('downloadModal.hdTitle')}</h3>
                <div className="price">{formatPrice(0, pricing.currency, lang)} <span>{t('pricing.perVideo')}</span></div>
                <ul>
                  <li><span className="check-icon">✓</span> {t('downloadModal.hdItem1')}</li>
                  <li><span className="check-icon">✓</span> {t('downloadModal.hdItem2')}</li>
                </ul>
                <button
                  className="btn btn-secondary modal-option-btn"
                  disabled={!outputs?.fresh}
                  onClick={() => { setShowDownloadModal(false); downloadCompletedVideo(repoUuid, outputs.hd); }}
                >
                  {!outputs?.fresh ? t('downloadModal.preparing') : t('downloadModal.downloadHdFree')}
                </button>
              </div>
              <div className="pricing-card featured">
                <h3>{t('downloadModal.fullResTitle')}</h3>
                <div className="price">{formatPrice(pricing.priceCents, pricing.currency, lang)} <span>{t('pricing.perVideo')}</span></div>
                <ul>
                  <li><span className="check-icon">✓</span> {t('downloadModal.fullResItem1')}</li>
                  <li><span className="check-icon">✓</span> {t('downloadModal.fullResItem2')}</li>
                </ul>
                <button
                  className="btn btn-primary modal-option-btn"
                  disabled={!outputs?.fresh}
                  onClick={() => {
                    const free = outputs.paid || outputs.full === outputs.hd;
                    if (free) {
                      setShowDownloadModal(false);
                      downloadCompletedVideo(repoUuid, outputs.full);
                    } else {
                      startCheckout(repoUuid);
                    }
                  }}
                >
                  {!outputs?.fresh
                    ? t('downloadModal.preparing')
                    : (outputs.paid || outputs.full === outputs.hd) ? t('downloadModal.downloadFull') : t('downloadModal.downloadFullFor', formatPrice(pricing.priceCents, pricing.currency, lang))}
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      <div className="frame-display-container">

      {loading ? (
        <div className="loading">
          <div>{t('frameDisplay.loadingFrames')}</div>
        </div>
      ) : (
        <>
          {/* Task toasts — stacked top-right */}
          {(() => {
            const toasts = [
              ...runningTasks.map(rt => ({ key: rt.id, type: 'interpolation', task: rt, onDismiss: () => setRunningTasks(prev => prev.filter(x => x.id !== rt.id)) })),
              ...(parentDecompositionTask ? [{ key: 'decomp', type: 'decomposition', task: parentDecompositionTask, onDismiss: () => setDecompositionTask(null) }] : []),
              ...(parentRecompositionTask ? [{ key: 'recomp', type: 'recomposition', task: parentRecompositionTask, onDismiss: () => setRecompositionTask(null) }] : []),
            ];
            return toasts.map(({ key, type, task, onDismiss }, index) => {
              const n = task.frames?.length ?? 0;
              const label = type === 'interpolation'
                ? { submitting: t('toast.fixing', n), running: t('toast.fixing', n), processing: t('toast.fixing', n), completed: t('toast.fixed', n), failed: t('toast.fixFailed') }[task.status] ?? t('toast.processing')
                : type === 'decomposition'
                ? { submitting: t('toast.analysing'), processing: t('toast.extracting'), completed: t('toast.framesReady'), failed: t('toast.extractionFailed') }[task.status] ?? t('toast.processing')
                : { submitting: t('toast.exporting'), processing: t('toast.exporting'), completed: t('toast.videoReady'), failed: t('toast.exportFailed') }[task.status] ?? t('toast.processing');
              const progress = task.progress && task.progress.total > 0
                ? Math.round((task.progress.current / task.progress.total) * 100)
                : null;
              return (
                <div key={key} className={`task-toast task-toast-${task.status}`} style={{ top: `${80 + index * 72}px` }}>
                  <div className="task-toast-row">
                    <span className="task-toast-dot" />
                    <span className="task-toast-label">{label}</span>
                    <button className="task-toast-close" onClick={onDismiss} aria-label={t('toast.dismiss')}>×</button>
                  </div>
                  {progress !== null && task.status !== 'completed' && task.status !== 'failed' && (
                    <div className="task-toast-bar">
                      <div className="task-toast-fill" style={{ width: `${progress}%` }} />
                    </div>
                  )}
                </div>
              );
            });
          })()}

          {selectedFrameNumbers.length > 0 && (
            <div className="selected-frames-banner">
              <div className="banner-frames">
                {selectedFrameNumbers.map(n => (
                  <span key={n} className="banner-chip">
                    #{n}
                    <button
                      className="banner-chip-remove"
                      onClick={() => setSelectedFrames(prev => { const s = new Set(prev); s.delete(n); return s; })}
                      aria-label={t('banner.deselect', n)}
                    >×</button>
                  </span>
                ))}
              </div>
              <div className="banner-divider" />
              <div className="banner-actions">
                <button className="banner-clear-btn" onClick={() => setSelectedFrames(new Set())}>
                  {t('banner.clear')}
                </button>
                <button
                  className="banner-run-btn"
                  onClick={async () => {
                    const taskId = `temp-${Date.now()}`;
                    try {
                      setRunningTasks(prev => [...prev, {
                        id: taskId, status: 'submitting',
                        frames: [...selectedFrameNumbers], startTime: Date.now()
                      }]);
                      setSelectedFrames(new Set());
                      const response = await fetch(`${API_BASE_URL}/api/${repoUuid}/interpolate/`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ target_frames: selectedFrameNumbers })
                      });
                      if (response.ok) {
                        const result = await response.json();
                        setRunningTasks(prev => prev.map(rt =>
                          rt.id === taskId ? { ...rt, id: result.task_id, status: 'running' } : rt
                        ));
                      } else {
                        alert(t('toast.errorStart', await response.text()));
                        setRunningTasks(prev => prev.filter(rt => rt.id !== taskId));
                      }
                    } catch (error) {
                      alert(t('toast.errorNetwork', error.message));
                      setRunningTasks(prev => prev.filter(rt => rt.id !== taskId));
                    }
                  }}
                >
                  {t('banner.fix', selectedFrameNumbers.length)}
                </button>
              </div>
            </div>
          )}

          <div className="frames-grid" key={`frames-${forceRefreshCounter}-${currentPage}`}>
            {frames.map((framePath, index) => {
              const frameNumber = frameNumbers[index] !== undefined ? frameNumbers[index] : (currentPage - 1) * framesPerPage + index + 1;
              const isSelected = selectedFrames.has(frameNumber);
              const isCorrected = correctedFrames.has(frameNumber);

              return (
                <div
                  key={index}
                  className={`frame-container ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleFrameSelection(frameNumber)}
                  style={{ cursor: 'pointer' }}
                >
                <img
                  src={peekFrame === frameNumber
                    ? `${API_BASE_URL}/api/${repoUuid}/frames/${frameNumber}/original`
                    : `${API_BASE_URL}/${framePath}?cb=${cacheBuster}&fc=${forceRefreshCounter}`}
                    alt={`Frame ${frameNumber}`}
                  className="frame-image"
                  loading="lazy"
                  onError={(e) => {
                    console.error(`Failed to load image: ${framePath}`);
                    e.target.style.display = 'none';
                  }}
                />
                <div className="frame-badges">
                  <button
                    className="frame-badge"
                    title={isCorrected ? t('compareModal.zoomCompare') : t('compareModal.zoom')}
                    onClick={(e) => { e.stopPropagation(); setCompareFrame(frameNumber); }}
                  >
                    <ZoomIcon />
                  </button>
                  {isCorrected && (
                    <button
                      className="frame-badge"
                      title={t('compareModal.peekTitle')}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => { e.stopPropagation(); setPeekFrame(frameNumber); }}
                      onMouseUp={() => setPeekFrame(null)}
                      onMouseLeave={() => setPeekFrame(null)}
                      onTouchStart={(e) => { e.stopPropagation(); setPeekFrame(frameNumber); }}
                      onTouchEnd={() => setPeekFrame(null)}
                    >
                      <CompareIcon />
                    </button>
                  )}
                  {isCorrected && (
                    <button
                      className="frame-badge"
                      title={t('compareModal.restoreTitle')}
                      disabled={restoring}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(t('compareModal.restoreConfirm', frameNumber))) {
                          restoreFrame(frameNumber);
                        }
                      }}
                    >
                      <RestoreIcon />
                    </button>
                  )}
                </div>
                {peekFrame === frameNumber && <span className="peek-tag">{t('compareModal.original')}</span>}
                <div className="frame-info">
                    <span>#{frameNumber}</span>
                    {isCorrected && <span className="corrected-dot" />}
                    {fps && <span className="frame-time">{formatFrameTime((frameNumber - 1) / fps)}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {totalFrames > framesPerPage && (
            <div className="pagination">
              <button
                onClick={goToPrevPage}
                disabled={currentPage === 1}
                className="page-button"
              >
                {t('pagination.previous')}
              </button>
              {Array.from({ length: Math.ceil(totalFrames / framesPerPage) }, (_, i) => i + 1)
                .filter(page => page === 1 || page === Math.ceil(totalFrames / framesPerPage) ||
                               (page >= currentPage - 2 && page <= currentPage + 2))
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="page-ellipsis">...</span>
                    )}
                    <button
                      onClick={() => goToPage(page)}
                      className={`page-button ${currentPage === page ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}
              <button
                onClick={goToNextPage}
                disabled={currentPage === Math.ceil(totalFrames / framesPerPage)}
                className="page-button"
              >
                {t('pagination.next')}
              </button>
            </div>
          )}

        </>
      )}

      {error && (
        <div className="error">
          {error}
        </div>
      )}
      </div>

      {/* Footer */}
      <footer>
        <div className="footer-bottom">
          <p>{t('footer.copyright')}</p>
        </div>
      </footer>
    </div>
  );
}

// Remount FrameDisplay whenever the uuid changes so switching projects resets
// all page state (frame cache, selection, pagination, outputs, ...)
function FrameDisplayRoute(props) {
  const { repoUuid } = useParams();
  return <FrameDisplay key={repoUuid} {...props} />;
}

function App() {
  const [decompositionTask, setDecompositionTask] = useState(null);
  const [recompositionTask, setRecompositionTask] = useState(null);

  const handleUploadComplete = useCallback((response) => {
    // Start tracking decomposition task; FrameDisplay polls it once mounted
    if (response.task_id) {
      console.log(`🎬 Decomposition task started: ${response.task_id}`);
      setDecompositionTask({
        id: response.task_id,
        status: 'processing',
        startTime: Date.now()
      });
    }
  }, []);

  return (
    <LanguageProvider>
      <Routes>
        <Route path="/" element={<UploadInterface onUploadComplete={handleUploadComplete} />} />
        <Route path="/auth/verify" element={<AuthVerify />} />
        <Route path="/:repoUuid" element={<FrameDisplayRoute decompositionTask={decompositionTask} setDecompositionTask={setDecompositionTask} recompositionTask={recompositionTask} setRecompositionTask={setRecompositionTask} />} />
      </Routes>
    </LanguageProvider>
  );
}

export default App; 